/* global __HOT__ */
// @jsx h
import domready from 'domready'
import { h, render } from 'preact'
import { toArray } from '@nrk/dh-utils'
import { createErrorReporter } from '../lib/errorReporting'
import { App } from './components/App'
import { root as rootElementClassName } from './components/App.css'
import { createGoogleAnalyticsTracker } from '@nrk/dh-analytics-utils'
import { getContextId } from '../lib/getContextId'
import { Provider as CustomProvider } from '../lib/provider'
import createHistory from 'history/createHashHistory'
import { matchRoute } from '../lib/matchroute.js'

window[rootElementClassName] = window[rootElementClassName] || {}

const ROUTES = [{ path: '/filter/:filter' }, { path: '/' }]

function getParams (location, defaultAntall = 10) {
  if (!location) {
    return {}
  }

  const route = matchRoute(ROUTES, location.pathname + location.search)
  if (!route) {
    return {}
  }
  const filter = (route.params.find(param => param.key === 'filter') || {}).value
  const id = (route.params.find(param => param.key === 'id') || {}).value
  const antallString = (route.params.find(param => param.key === 'antall') || {}).value
  const antall = antallString ? Number.parseInt(antallString) : defaultAntall
  return { filter, antall, id }
}

function init () {
  let history
  let historyUnlisten

  findElements(rootElementClassName).forEach(el => {
    const id = el.getAttribute('data-id')
    el.id = id
    window[rootElementClassName][id] = true

    // Extract values sent from server
    const name = el.getAttribute('data-name')
    const preloadedState = window.faqPreloadedState[id] // JSON.parse(el.getAttribute('data-preloaded-state'))
    const errorTrackingEnabled = el.getAttribute('data-error-tracking-enabled') === 'true'
    const needsHistory = preloadedState.showQuestions

    // setup error reporting
    const simpleErrorReport = createErrorReporter(name, errorTrackingEnabled)
    simpleErrorReport.listenAndCatch()

    // Extract metadata required to create google analytics tracker
    const gaPropertyId = el.getAttribute('data-ga-property-id')
    const metadata = JSON.parse(el.getAttribute('data-metadata'))
    const { title, selfId, published } = metadata

    // create google analytics tracker
    const googleAnalyticsTracker = createGoogleAnalyticsTracker({
      name: 'dh',
      gaPropertyId,
      self: {
        id: selfId,
        title,
        publishedDate: published ? new Date(published) : null
      },
      context: {
        id: getContextId(document.location.href)
      }
    })

    const sendError = (message) => {
      simpleErrorReport.sendErrorReport({ message })
    }

    const sendGaEvent = (eventObj) => {
      googleAnalyticsTracker.sendEvent({
        category: `dh-modul-${name}`,
        ...eventObj
      })
    }

    let params = {}
    if (needsHistory) {
      history = history || createHistory()
      params = getParams(history.location, preloadedState.maxQuestions)
    }

    // ... and render with the exact same props as on server
    render(
      <CustomProvider sendGaEvent={sendGaEvent} sendError={sendError}>
        <App {...preloadedState} filter={params.filter} antall={params.antall} id={params.id} />
      </CustomProvider>, el, el.firstChild
    )

    if (params.filter) {
      // If filter was set on load, scroll to answers
      const tagElement = document.querySelector('.dh-dh-orderud-faq-tagWrapper')
      tagElement && tagElement.scrollIntoView(true)
    }

    if (needsHistory) {
      historyUnlisten = history.listen((location) => {
        const params = getParams(location, preloadedState.maxQuestions)

        render(
          <CustomProvider sendGaEvent={sendGaEvent} sendError={sendError}>
            <App {...preloadedState} filter={params.filter} antall={params.antall} id={params.id} />
          </CustomProvider>, el, el.firstChild
        )
      })
    }

    // Enable webpack hot-reloading
    if (__HOT__) {
      historyUnlisten && historyUnlisten()
      // Reload and re-render preact application
      module.hot.accept('./client', () => {
        const { App } = require('./components/App')
        const { Provider: CustomProvider } = require('../lib/provider')
        render(
          <CustomProvider sendGaEvent={sendGaEvent} sendError={sendError}>
            <App {...preloadedState} filter={params.filter} antall={params.antall} />
          </CustomProvider>, el, el.firstChild)
      })

      if (needsHistory) {
        historyUnlisten = history.listen((location) => {
          const params = getParams(location, preloadedState.maxQuestions)

          render(
            <CustomProvider sendGaEvent={sendGaEvent} sendError={sendError}>
              <App {...preloadedState} filter={params.filter} antall={params.antall} />
            </CustomProvider>, el, el.firstChild
          )
        })
      }
    }
  })
}

if (__HOT__) {
  require('preact/debug')
}

function findElements (className) {
  return toArray(document.getElementsByClassName(className)).filter(el => !isInitialized(el, className))
}

function isInitialized (el, className) {
  return el.id && window[className][el.id]
}

domready(init)
